import type { LucideIcon } from 'lucide-react'
import type { FlowerSubscriptionIndexSearch } from '@/lib/services/flower-subscriptions/flower-subscriptions.schema'
import type {
  FlowerSubscriptionIndexResponse,
  FlowerSubscriptionListItem,
} from '@/lib/services/flower-subscriptions/flower-subscriptions.types'

import IfGlobalOrg from '@/components/organizations/IfGlobalOrg'
import { OrganizationContextNotice } from '@/components/patterns/info-notice'
import { AdminPageHeader } from '@/components/patterns/admin-page'
import { FlowerSubscriptionsIndexTable } from '@/components/subscriptions/subscription-display'
import {
  IndexTableClearFiltersButton,
  IndexTableSearchBox,
  IndexTableToolbar,
} from '@/components/tables/IndexTable'
import { useIndexTableSearchParams } from '@/components/tables/useIndexTableSearchParams'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import queryClient from '@/lib/queryClient'
import { prefetchIndexTableRouteQuery } from '@/lib/services/index-table-query-options'
import {
  flowerSubscriptionQueries,
  useFlowerSubscriptions,
} from '@/lib/services/flower-subscriptions/flower-subscriptions.query'
import {
  flowerSubscriptionIndexSearchSchema,
  getFlowerSubscriptionIndexFilters,
  getFlowerSubscriptionIndexSearchParams,
  strippedFlowerSubscriptionIndexSearchParams,
} from '@/lib/services/flower-subscriptions/flower-subscriptions.schema'
import {
  createFileRoute,
  notFound,
  stripSearchParams,
  useNavigate,
  type SearchSchemaInput,
} from '@tanstack/react-router'
import {
  CalendarClock,
  CalendarDays,
  CreditCard,
  Repeat2,
  SlidersHorizontal,
  Truck,
} from 'lucide-react'
import { useMemo, useState } from 'react'

// Search values enter through the schema; its defaults make every validated field available.
type SubscriptionSearchInput = Partial<Record<keyof FlowerSubscriptionIndexSearch, unknown>>

const DEFAULT_PAGE_SIZE = 20

const statusFilterOptions = [
  { value: 'all', label: 'All statuses', icon: SlidersHorizontal },
  { value: 'skipped', label: 'Next order skipped', icon: CalendarClock },
  { value: 'active', label: 'Active', icon: Repeat2 },
  { value: 'payment_failed', label: 'Payment Failed', icon: CreditCard },
  { value: 'fulfillment_unavailable', label: 'Fulfillment Unavailable', icon: SlidersHorizontal },
  { value: 'payment_review', label: 'Payment Needs Review', icon: CreditCard },
  { value: 'cancelled', label: 'Cancelled', icon: SlidersHorizontal },
]

const frequencyFilterOptions = [
  { value: 'all', label: 'All frequencies', icon: SlidersHorizontal },
  { value: 'weekly', label: 'Weekly', icon: Repeat2 },
  { value: 'biweekly', label: 'Biweekly', icon: Repeat2 },
  { value: 'monthly', label: 'Monthly', icon: Repeat2 },
  { value: 'every_3_months', label: 'Every 3 months', icon: Repeat2 },
  { value: 'every_6_months', label: 'Every 6 months', icon: Repeat2 },
  { value: 'yearly', label: 'Yearly', icon: Repeat2 },
]

const fulfillmentFilterOptions = [
  { value: 'all', label: 'All fulfillment', icon: SlidersHorizontal },
  { value: 'pickup', label: 'Pickup', icon: CalendarDays },
  { value: 'delivery', label: 'Delivery', icon: Truck },
]

const sortOptions = [
  { value: 'attention', label: 'Operational priority', icon: SlidersHorizontal },
  { value: 'next_fulfillment_asc', label: 'Next fulfillment', icon: CalendarDays },
  { value: 'next_charge_asc', label: 'Next charge', icon: CalendarClock },
  { value: 'customer_asc', label: 'Purchaser A-Z', icon: SlidersHorizontal },
  { value: 'package_asc', label: 'Package A-Z', icon: Repeat2 },
  { value: 'created_desc', label: 'Newest', icon: CalendarClock },
]

export const Route = createFileRoute('/_authenticated/$orgId/subscriptions/')({
  component: RouteComponent,
  validateSearch: (search: SubscriptionSearchInput & SearchSchemaInput) =>
    flowerSubscriptionIndexSearchSchema.parse(search),
  search: {
    middlewares: [
      stripSearchParams<SubscriptionSearchInput>(strippedFlowerSubscriptionIndexSearchParams),
    ],
  },
  loader: async ({ context, params }) => {
    if (!context.ability.can('list', 'flower-subscriptions')) throw notFound()

    void prefetchIndexTableRouteQuery(
      context.queryClient,
      flowerSubscriptionQueries.index({
        organizationId: params.orgId,
        filters: getFlowerSubscriptionIndexFilters({}),
        page: 1,
        pageSize: DEFAULT_PAGE_SIZE,
      })
    )
  },
})

function getSubscriptionsFromResponse(
  response: FlowerSubscriptionIndexResponse | FlowerSubscriptionListItem[] | null | undefined
): FlowerSubscriptionListItem[] {
  return Array.isArray(response) ? response : (response?.data ?? [])
}

function subscriptionCountText(total: number): string {
  return `${total} subscription${total === 1 ? '' : 's'}`
}

function SubscriptionSelect({
  value,
  onValueChange,
  options,
  label,
}: {
  value: string
  onValueChange: (value: string) => void
  options: readonly { value: string; label: string; icon: LucideIcon }[]
  label: string
}) {
  const isFiltered = value !== 'all' && value !== 'attention'

  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger aria-label={label} selected={isFiltered} className="w-fit">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => {
          const Icon = option.icon

          return (
            <SelectItem key={option.value} value={option.value}>
              <Icon className="size-4" />
              {option.label}
            </SelectItem>
          )
        })}
      </SelectContent>
    </Select>
  )
}

function RouteComponent() {
  const { orgId } = Route.useParams()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const [showGlobalAlert, setShowGlobalAlert] = useState(true)
  const urlSearch = getFlowerSubscriptionIndexSearchParams(search)
  const subscriptionFilters = useMemo(
    () => getFlowerSubscriptionIndexFilters(urlSearch),
    [urlSearch]
  )
  const {
    data: response,
    isPending,
    isFetching,
  } = useFlowerSubscriptions({
    organizationId: orgId,
    filters: subscriptionFilters,
    page: urlSearch.page,
    pageSize: urlSearch.page_size,
  })
  const subscriptions = getSubscriptionsFromResponse(response)
  const paginationMeta = response?.meta ?? {
    current_page: urlSearch.page,
    page_size: urlSearch.page_size,
    total: subscriptions.length,
    last_page: 1,
  }
  const hasClearableFilters =
    Boolean(urlSearch.search?.trim()) ||
    urlSearch.status_filter !== 'all' ||
    urlSearch.frequency_filter !== 'all' ||
    urlSearch.fulfillment_type_filter !== 'all'

  const { updateSearchTerm, updateSort, updatePage, updatePageSize, updateTableParams } =
    useIndexTableSearchParams({
      navigate,
      getSearchParams: getFlowerSubscriptionIndexSearchParams,
      searchTerm: urlSearch.search ?? '',
    })

  const clearFilters = () => {
    updateTableParams({
      search: undefined,
      status_filter: 'all',
      frequency_filter: 'all',
      fulfillment_type_filter: 'all',
    })
  }

  const handlePageIntent = (page: number) => {
    void queryClient.prefetchQuery(
      flowerSubscriptionQueries.index({
        organizationId: orgId,
        filters: subscriptionFilters,
        page,
        pageSize: urlSearch.page_size,
      })
    )
  }

  return (
    <div className="space-y-4">
      <IfGlobalOrg>
        {showGlobalAlert ? (
          <OrganizationContextNotice className="mb-4" onDismiss={() => setShowGlobalAlert(false)}>
            You are currently viewing subscriptions for all organizations.
          </OrganizationContextNotice>
        ) : null}
      </IfGlobalOrg>

      <AdminPageHeader
        icon={Repeat2}
        title="Subscriptions"
        description="Track active customer subscriptions, payment issues, skipped orders, and upcoming fulfillment."
      />

      <FlowerSubscriptionsIndexTable
        subscriptions={subscriptions}
        response={response}
        page={urlSearch.page}
        pageSize={urlSearch.page_size}
        sort={urlSearch.sort}
        onSortChange={updateSort}
        onPageChange={updatePage}
        onPageIntent={handlePageIntent}
        onPageSizeChange={updatePageSize}
        isLoading={isPending}
        toolbar={
          <IndexTableToolbar
            primary={
              <IndexTableSearchBox
                value={urlSearch.search ?? ''}
                onCommit={updateSearchTerm}
                countText={subscriptionCountText(paginationMeta.total)}
                isFetching={isFetching}
                placeholder="Search subscriptions..."
              />
            }
            secondary={
              <>
                {hasClearableFilters ? (
                  <IndexTableClearFiltersButton onClick={clearFilters} />
                ) : null}
                <SubscriptionSelect
                  value={urlSearch.status_filter}
                  onValueChange={(status_filter) => updateTableParams({ status_filter })}
                  options={statusFilterOptions}
                  label="Filter subscription status"
                />
                <SubscriptionSelect
                  value={urlSearch.frequency_filter}
                  onValueChange={(frequency_filter) => updateTableParams({ frequency_filter })}
                  options={frequencyFilterOptions}
                  label="Filter subscription frequency"
                />
                <SubscriptionSelect
                  value={urlSearch.fulfillment_type_filter}
                  onValueChange={(fulfillment_type_filter) =>
                    updateTableParams({ fulfillment_type_filter })
                  }
                  options={fulfillmentFilterOptions}
                  label="Filter subscription fulfillment"
                />
                <div className="lg:hidden">
                  <SubscriptionSelect
                    value={urlSearch.sort}
                    onValueChange={updateSort}
                    options={sortOptions}
                    label="Sort subscriptions"
                  />
                </div>
              </>
            }
          />
        }
      />
    </div>
  )
}
